import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Calendar, MapPin, Play, Edit, Trash2, Archive } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';

const EventCard = ({ event, onStatusChange, onNavigate, onDelete, onArchive }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const getStatusColor = (status) => {
    switch(status) {
      case 'live': return 'bg-green-100 text-green-700 border-green-200';
      case 'ended': return 'bg-gray-100 text-gray-600 border-gray-200';
      case 'archived': return 'bg-blue-100 text-blue-600 border-blue-200';
      default: return 'bg-violet-100 text-violet-700 border-violet-200';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'live': return '🔴 En direct';
      case 'ended': return '✓ Terminé';
      case 'archived': return '📦 Archivé';
      default: return '📝 Brouillon';
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(false);
    onDelete(event.id);
  };

  const handleArchive = () => {
    setShowArchiveDialog(false);
    onArchive(event.id);
  };

  return (
    <div
      data-testid={`event-card-${event.id}`}
      className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:border-violet-300 hover:shadow-lg transition-all duration-200"
    >
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900 flex-1">
          {event.name}
        </h3>
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(event.status)}`}>
          {getStatusLabel(event.status)}
        </span>
      </div>

      <div className="space-y-2 mb-6">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Calendar className="w-4 h-4" />
          <span>{new Date(event.date).toLocaleDateString('fr-FR')}</span>
        </div>
        {event.location && (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <MapPin className="w-4 h-4" />
            <span>{event.location}</span>
          </div>
        )}
      </div>

      {/* Status Control Buttons */}
      {event.status === 'draft' && (
        <div className="mb-3">
          <Button
            data-testid={`start-event-button-${event.id}`}
            onClick={() => onStatusChange(event.id, 'live')}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
          >
            <Play className="w-4 h-4 mr-2" />
            Démarrer l'événement
          </Button>
        </div>
      )}
      {event.status === 'live' && (
        <div className="mb-3">
          <Button
            data-testid={`end-event-button-${event.id}`}
            onClick={() => onStatusChange(event.id, 'ended')}
            variant="outline"
            className="w-full border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
          >
            ✓ Terminer l'événement
          </Button>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          data-testid={`edit-event-button-${event.id}`}
          onClick={() => onNavigate(`/events/${event.id}`)}
          variant="outline"
          className="flex-1 border-violet-200 text-violet-600 hover:bg-violet-50 hover:border-violet-300 transition-all duration-200"
        >
          <Edit className="w-4 h-4 mr-2" />
          Gérer
        </Button>
        <Button
          data-testid={`dj-interface-button-${event.id}`}
          onClick={() => onNavigate(`/dj/${event.id}`)}
          disabled={event.status === 'ended' || event.status === 'archived'}
          className={`flex-1 ${
            event.status === 'ended' || event.status === 'archived'
              ? 'bg-gray-300 cursor-not-allowed opacity-50'
              : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-md hover:shadow-lg'
          } text-white transition-all duration-200`}
        >
          <Play className="w-4 h-4 mr-2" />
          Interface DJ
        </Button>
      </div>

      {/* Delete/Archive buttons */}
      <div className="flex gap-2 mt-3">
        {event.status === 'draft' && (
          <Button
            data-testid={`delete-event-button-${event.id}`}
            onClick={() => setShowDeleteDialog(true)}
            variant="outline"
            size="sm"
            className="flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition-all duration-200"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Supprimer
          </Button>
        )}
        {(event.status === 'live' || event.status === 'ended') && (
          <Button
            data-testid={`archive-event-button-${event.id}`}
            onClick={() => setShowArchiveDialog(true)}
            variant="outline"
            size="sm"
            className="flex-1 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200"
          >
            <Archive className="w-3 h-3 mr-1" />
            Archiver
          </Button>
        )}
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Supprimer l'événement ?"
        description={`Êtes-vous sûr de vouloir supprimer "${event.name}" ? Cette action est irréversible et supprimera toutes les données associées (parties, playlists, morceaux).`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={handleDelete}
        destructive
      />

      <ConfirmDialog
        open={showArchiveDialog}
        onOpenChange={setShowArchiveDialog}
        title="Archiver l'événement ?"
        description={`Êtes-vous sûr de vouloir archiver "${event.name}" ? L'événement sera masqué mais les données seront conservées.`}
        confirmLabel="Archiver"
        cancelLabel="Annuler"
        onConfirm={handleArchive}
      />
    </div>
  );
};

export default EventCard;
